import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { AuthKitProvider } from '@workos-inc/authkit-react';
import './styles/global.css';
import './styles/blue-scheme.css';
import { SocketBridge } from '~portal/agents';
import { ApiProvider } from '~portal/api';
import { RouteErrorFallback, RoutePending } from '~portal/components/common';
import { getAppEnv } from '~portal/env';
import {
  clearTokenCookie,
  CookieConsentBanner,
  cssVariablesResolver,
  DatadogRUM,
  logger,
  pageScrollToTopSelectors,
  setPendingReturnTo,
  theme,
  writeTokenCookie,
} from '~portal/lib';
import { PrimitiveStyles } from '~ui';
import { routeTree } from './routeTree.gen';

// Create router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Route chunks are lazy (autoCodeSplitting, docs/code-splitting.md): a chunk that will not load
  // lands here with Reload and Try again, and a slow one shows a loader after the router's
  // default pending delay instead of a page that seems not to respond.
  defaultErrorComponent: RouteErrorFallback,
  defaultPendingComponent: RoutePending,
  // Pages scroll inside the content column, not the window (lib/page-scroll.ts): without these
  // a client-side navigation kept the previous page's scroll position until a reload.
  scrollToTopSelectors: pageScrollToTopSelectors,
});

// Register router for type safety
declare module '@tanstack/react-router' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Module augmentation requires interface
  interface Register {
    router: typeof router;
  }
}

/**
 * @desc Resolves the `returnTo` path stashed in AuthKit's redirect `state`, rejecting
 * anything that doesn't resolve to a same-origin destination. `state` round-trips
 * through the OAuth redirect as plaintext and is not integrity protected.
 */
function resolveReturnTo(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) return undefined;

  const returnTo = (state as Record<string, unknown>).returnTo;
  if (typeof returnTo !== 'string') return undefined;

  let url: URL;
  try {
    url = new URL(returnTo, window.location.origin);
  } catch {
    return undefined;
  }

  return url.origin === window.location.origin
    ? `${url.pathname}${url.search}${url.hash}`
    : undefined;
}

export function App() {
  const env = getAppEnv();

  return (
    <AuthKitProvider
      clientId={env.workosClientId}
      apiHostname={env.workosApiHostname}
      devMode={!env.workosApiHostname}
      // Always name this deployment's own callback explicitly: relying on the environment
      // default redirect URI strands every other deployment's sign-in on the wrong host. The
      // BARE origin, no trailing slash — WorkOS matches the allowed-redirect-URI list exactly,
      // and the registered entries are bare origins; `${origin}/` was rejected with "This is
      // not a valid redirect URI", breaking every staging sign-in (2026-08-25).
      redirectUri={window.location.origin}
      onRedirectCallback={({ state }) => {
        const returnTo = resolveReturnTo(state);
        // Park the deep link for the protected layout to restore once auth has settled (see
        // return-to.ts). Never navigate from here (task 9.11): AuthKit calls this and then, in
        // the same tick, `history.replaceState({}, '', <the callback URL without code/state>)`.
        // TanStack's history intercepts that replace and snaps the router back to '/', while its
        // own deferred pushState still writes the deep link into the address bar — the router
        // and the URL disagree, and the one-shot restore could see the deep link as "already
        // there" and drop it. A bare '/' never parks: a retried roundtrip from the root must not
        // clobber a real deep link parked by the first attempt.
        if (returnTo && returnTo !== '/') setPendingReturnTo(returnTo);
      }}
      // Fires on the initial code exchange and on every silent refresh thereafter, carrying the
      // new access token — the one hook AuthKit gives us over a token it otherwise keeps in
      // memory, and so where the edge middleware's cookie mirror is kept current (design D9).
      onRefresh={({ accessToken }) => writeTokenCookie(accessToken)}
      // `signIn` rejects where the browser blocks the storage AuthKit needs for its
      // PKCE verifier; log it rather than leaving an unhandled rejection. The route
      // guards then take over and offer a retry (see `useSignInRedirect`).
      onRefreshFailure={({ signIn }) => {
        clearTokenCookie();
        signIn().catch((error: unknown) =>
          logger.error('sign-in after refresh failure', { error }),
        );
      }}
    >
      <CookieConsentBanner />
      <DatadogRUM />
      <ApiProvider>
        <SocketBridge />
        <MantineProvider
          theme={theme}
          defaultColorScheme="dark"
          cssVariablesResolver={cssVariablesResolver}
        >
          {/* The shared primitives' hover/focus states (internal/ui), keyed on data-ui-* hooks so
              fragment markup from the insights build picks them up too (ADR-16). */}
          <PrimitiveStyles />
          <ModalsProvider>
            <RouterProvider router={router} />
          </ModalsProvider>
        </MantineProvider>
      </ApiProvider>
    </AuthKitProvider>
  );
}
